import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Gift } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

const TIERS = [
  {
    name: "Single Case", price: "$95", unit: "per case",
    tagline: "For dentists getting started with specialist planning.",
    features: [
      "Initial digital setup, upper & lower arch",
      "Complete staging plan",
      "Attachment planning",
      "IPR planning",
      "Bite-correction strategy where applicable",
      "Planned to your prescribed treatment objectives",
      "3D viewer review in your portal",
      "1 revision round included",
      "Final approved setup",
      "Standard 24h turnaround",
    ],
    cta: "Submit a Case", featured: false, to: "/register",
  },
  {
    name: "Clinic Plan", price: "$690", unit: "per month",
    badge: "Save $260/month vs pay-as-you-go",
    tagline: "For growing clinics planning cases every week.",
    features: [
      "Up to 10 cases per month",
      "Everything in Single Case, plus:",
      "Priority planning queue",
      "2 revision rounds per case",
      "Case dashboard & full clinic history",
      "Dedicated support contact",
      "Monthly case reporting",
    ],
    cta: "Start Clinic Plan", featured: true, to: "/register",
  },
  {
    name: "Lab & Enterprise", price: "Custom", unit: "volume-based",
    tagline: "For aligner brands, dental labs, DSOs and multi-location groups.",
    features: [
      "100 / 500 / 1,000+ cases per month",
      "SLA-backed turnaround commitments",
      "White-label planning workflow",
      "Dedicated account manager",
      "Structured escalation & QC audits",
      "Volume pricing that improves with scale",
    ],
    cta: "Talk to Us", featured: false, to: "/enterprise",
  },
];

export default function Pricing() {
  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <SectionTag>Pricing</SectionTag>
            <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.6rem,6.5vw,5.5rem)] max-w-3xl">
              <LineMask delay={0.1}>Simple plans.</LineMask>
              <LineMask delay={0.25} className="text-neon">Serious planning.</LineMask>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-8 max-w-xl text-base md:text-lg text-slate-400">Transparent per-case pricing. No setup fees, no software licences, no long-term lock-in.</p>
            </FadeIn>
          </div>
        </section>

        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <FadeIn>
              <div className="rounded-2xl border border-neon/40 bg-neon/[0.06] p-6 lg:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="free-first-case-banner">
                <span className="h-12 w-12 rounded-xl bg-neon flex items-center justify-center shrink-0"><Gift className="h-6 w-6 text-ink" /></span>
                <div className="flex-1">
                  <p className="font-display font-bold text-lg text-white">Your first case is free.</p>
                  <p className="text-sm text-slate-400 mt-1">Submit your first case at no charge and review our planning quality in 3D — before committing to anything.</p>
                </div>
                <Link to="/register" data-testid="free-first-case-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,229,255,0.4)]">
                  Start your free case <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-6">
            {TIERS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.1} className="h-full">
                <div className={`relative h-full rounded-2xl p-8 lg:p-10 border backdrop-blur-xl flex flex-col ${t.featured ? "bg-neon/[0.06] border-neon/50" : "bg-white/5 border-white/10"}`}>
                  {t.featured && (
                    <span className="absolute -top-3 left-8 rounded-full bg-neon px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink">Most popular</span>
                  )}
                  <h2 className="font-display font-bold text-xl">{t.name}</h2>
                  <p className="mt-2 text-xs text-slate-500 min-h-[2rem]">{t.tagline}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-display font-black text-5xl tracking-tight">{t.price}</span>
                    <span className="text-xs text-slate-500">{t.unit}</span>
                  </div>
                  {t.badge && (
                    <span className="mt-3 inline-flex w-fit rounded-full border border-neon/40 bg-neon/10 px-3 py-1 text-[11px] font-bold text-neon" data-testid="clinic-savings-badge">{t.badge}</span>
                  )}
                  <ul className="mt-8 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className={`flex items-start gap-3 text-sm ${f.endsWith(":") ? "text-slate-500 italic" : "text-slate-300"}`}>
                        {!f.endsWith(":") && <Check className="h-4 w-4 text-neon mt-0.5 shrink-0" />} {f}
                      </li>
                    ))}
                  </ul>
                  <Link to={t.to} data-testid={`pricing-${t.name.toLowerCase().replace(/\s|&/g, "-")}-btn`}
                    className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-transform duration-300 hover:scale-[1.03] ${t.featured ? "bg-neon text-ink hover:shadow-[0_0_30px_rgba(0,229,255,0.4)]" : "border border-white/20 text-white hover:border-neon hover:text-neon"}`}>
                    {t.cta} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn>
            <p className="mt-12 text-center text-xs text-slate-500">
              10 individual cases would cost $950 — the Clinic Plan saves you 27% every month. All plans include secure file handling, case tracking and email notifications. Prices in USD, exclusive of taxes.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-4 text-center text-sm text-slate-400">
              Questions about what's included? <Link to="/faq" className="text-neon hover:underline" data-testid="pricing-faq-link">Read the FAQ</Link> or <Link to="/contact" className="text-neon hover:underline">talk to the team</Link>.
            </p>
          </FadeIn>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
